/* istanbul ignore file */
import React, { useState, useMemo, useRef, ReactElement } from 'react'
import classnames from 'classnames/bind'
import GoogleMapReact from 'google-map-react'
import type { MapOptions, Maps, ClickEventValue } from 'google-map-react'
import configment from '@adc/configment'
import styles from './styles.scss'
import Icon from '../../elementals/icon'
// @ts-ignore next
const cx = classnames.bind(styles)

const GOOGLE_MAPS_LIBRARIES = ['places']
const US_CENTER_LAT_LNG = {
  lat: 39.8097343,
  lng: -98.5556199,
}
const DEFAULT_ZOOM_LEVEL = 18

// settings for initial render before we locate, fitbounds etc.
const defaultSettings = {
  center: US_CENTER_LAT_LNG,
  zoom: DEFAULT_ZOOM_LEVEL,
}

type SimpleMapProps = {
  defaultView?: ReactElement
  coords?: { lat: number; lng: number }
  createMapOptions: (maps: Maps) => MapOptions
  onMapClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  showStreetView?: boolean
}
const SimpleMap: React.FC<SimpleMapProps> = ({
  defaultView,
  coords,
  createMapOptions = () => ({}),
  onMapClick = () => undefined,
  showStreetView = false,
}: SimpleMapProps) => {
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false)
  const [streetViewAvailable, setStreetViewAvailable] = useState(true)
  const streetViewRef = useRef<HTMLDivElement | null>(null)

  const _createMapOptions = useMemo(() => {
    return (maps: Maps): MapOptions => {
      return {
        mapTypeControlOptions: {
          position: maps.ControlPosition.TOP_RIGHT,
        },
        mapTypeControl: true,
        fullscreenControl: false,
        streetViewControl: false,
        clickableIcons: false,
        zoomControl: false,
        gestureHandling: 'none',
        draggableCursor: 'pointer',
        ...createMapOptions(maps),
      }
    }
  }, [createMapOptions])

  const onGoogleApiLoaded = ({ map, maps }: { map: unknown; maps: Maps }) => {
    setGoogleMapsLoaded(true)

    if (!showStreetView) return
    // @ts-ignore next
    const streetView = new maps.StreetViewPanorama( // eslint-disable-line
      document.getElementById('street-view'),
      {
        position: coords,
        pov: { heading: 270, pitch: 0 },
        zoom: 1,
        disableDefaultUI: true, // Disables all default UI controls
        clickToGo: false, // Disables click-to-move behavior
      }
    )
    // @ts-ignore next
    map.setStreetView(streetView) // eslint-disable-line

    // @ts-ignore next
    const sv = new maps.StreetViewService() // eslint-disable-line
    // @ts-ignore next
    // eslint-disable-next-line
    sv.getPanorama(
      {
        location: { lat: coords?.lat, lng: coords?.lng },
        radius: 50,
      },
      (_: unknown, status: string) => {
        setStreetViewAvailable(status === maps.StreetViewStatus.OK)
      }
    )
  }

  if (showStreetView && !streetViewAvailable && defaultView) return defaultView

  return (
    <div className={cx('map-container')}>
      <GoogleMapReact
        bootstrapURLKeys={{
          client: configment.get('GOOGLE_MAP_CLIENT_ID', ''),
          language: 'en',
          version: 'weekly',
          libraries: GOOGLE_MAPS_LIBRARIES,
        }}
        center={coords ? { ...coords } : defaultSettings.center}
        defaultZoom={defaultSettings.zoom}
        options={_createMapOptions}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={onGoogleApiLoaded}
        onClick={onMapClick as unknown as (value: ClickEventValue) => void}
      >
        {googleMapsLoaded && coords && (
          <Marker onClick={onMapClick} {...coords} />
        )}
      </GoogleMapReact>
      {/* Street View */}
      {showStreetView && (
        <div
          id='street-view'
          // @ts-ignore next
          onClick={e => {
            const target = e.target as HTMLElement
            //Do not propagate click event if the click is on the street view controls
            if (target.closest('.gm-control-active')) {
              e.stopPropagation()
            } else {
              onMapClick(
                e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>
              )
            }
          }}
          ref={streetViewRef}
          className={cx('street-view', 'pointer')}
        />
      )}
    </div>
  )
}

type MarkerProps = {
  lat: number
  lng: number
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Marker = (_props: MarkerProps) => {
  const { onClick } = _props
  return (
    <div onClick={onClick} className={cx('marker-container')}>
      <Icon
        name='fa-location-dot'
        iconStyle='solid'
        size='huge'
        color='error'
        className={cx('marker')}
      />
    </div>
  )
}

export default SimpleMap
